import {
  FARM_DIAGNOSTICS_KEY_METRICS_LATEST_ROLLING_AVG_LABEL,
  FARM_DIAGNOSTICS_KEY_METRICS_TREND_LABEL,
  FARM_DIAGNOSTICS_KEY_METRICS_YOY_CHANGE_LABEL,
} from 'constants/uiConstants';
import React from 'react';
import { formatNumber } from '../Insights/summary2/formatNumber';
import type { KeyMetricsSummary, TrendFactor } from './computeFarmDiagnosticsMetrics';
import TrendBadge from './TrendBadge';

interface TrendKeyMetricsPanelProps {
  summary: KeyMetricsSummary;
  factors: TrendFactor[];
  formatValue: (v: number) => string;
  /** Appended after the signed YoY change number, e.g. 'kg/mm' or '$/mm'. */
  yoyUnit: string;
  /** Distinguishes this tile when more than one appears together (Bio vs Financial shows two side by side) — omit for a standalone lens where the tab/section title already gives context. */
  metricLabel?: string;
}

/**
 * Shared "Key Metrics" tile (latest 3-yr rolling avg / trend / YoY change)
 * used identically by WUE Trend, Financial Efficiency, and both halves of
 * Bio vs Financial — same fields, same layout, so a user comparing lenses
 * sees a consistent structure, not three different summary styles.
 *
 * Deliberately not a `SectionCard` — its title/heading row was redundant
 * (the tile's own field labels already say what it is), so this reproduces
 * just SectionCard's outer chrome without the `<h3>`.
 */
const TrendKeyMetricsPanel: React.FC<TrendKeyMetricsPanelProps> = ({
  summary,
  factors,
  formatValue,
  yoyUnit,
  metricLabel,
}) => (
  <div className="rounded-lg border border-gray-bright bg-white py-4 pl-4 pr-6">
    {metricLabel && (
      <span className="mb-3 block text-sm font-bold text-charcoal">{metricLabel}</span>
    )}
    <div className="flex flex-wrap gap-8">
      <div className="flex flex-col gap-1">
        <span className="text-sm text-gray-muted">
          {FARM_DIAGNOSTICS_KEY_METRICS_LATEST_ROLLING_AVG_LABEL}
        </span>
        <span className="text-lg font-bold text-charcoal">
          {summary.latestRollingAvg != null ? formatValue(summary.latestRollingAvg) : '—'}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-gray-muted">
          {FARM_DIAGNOSTICS_KEY_METRICS_TREND_LABEL}
        </span>
        <div className="flex items-center gap-2">
          <TrendBadge trend={summary.trend} factors={factors} />
          {summary.trendPct != null && (
            <span className="text-sm text-gray-muted">
              ({summary.trendPct > 0 ? '+' : ''}
              {formatNumber(summary.trendPct)}%)
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-gray-muted">
          {FARM_DIAGNOSTICS_KEY_METRICS_YOY_CHANGE_LABEL}
        </span>
        <span className="text-lg font-bold text-charcoal">
          {summary.yoyChange != null
            ? `${summary.yoyChange > 0 ? '+' : ''}${formatNumber(summary.yoyChange)} ${yoyUnit}`
            : '—'}
        </span>
      </div>
    </div>
  </div>
);

export default TrendKeyMetricsPanel;
